import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from '../../../../hooks'
import { OrganizationInterface } from '../../../../ts/interfaces'
import {
    ErrorMessage,
    FormField,
    FormInput40,
    PrimaryLabel,
} from '../../../styled/formElements'
import { HeadingPrimary } from '../../../styled/typography'
import { NextButton } from '../../buttons'
import {
    checkAddress,
    checkCity,
    checkCompanyName,
    checkZipCode,
    isValidAddress,
    isValidCompanyName,
    isValidZipCode,
} from '../../../../helpers/validation'
import CountryCodeDropdown from '../../form/CountryCodeDropdown'

const Container = styled.div`
    width: 70rem;
    margin: 0 auto;
    padding-top: 12rem;
    text-align: center;
    padding-bottom: 6rem;
`

const Form = styled.form`
    padding-top: 6.6rem;
    margin: 0 auto;
`

const FormContainer = styled.div`
    width: 100%;
    display: flex;
    gap: 16px;
`

const FormHalf = styled.div`
    width: 50%;
    text-align: left;
`

const FlexInputContainer = styled.div`
    display: flex;
    gap: 16px;
`

const CityInputContianer = styled.div`
    width: 60%;
`

const ZipInputContianer = styled.div`
    width: 40%;
`
const NextButtonContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    padding-top: 4rem;
`

interface CompanyFormFrameProps {
    organization: OrganizationInterface
    updateOrganization: (key: string, value: null | string | number) => void
    setCurrentPageNumber: (pageNumber: number) => void
    updateOrganizationCountry: (country: any) => void
}

const CompanyFormFrame: React.FC<CompanyFormFrameProps> = ({
    organization,
    updateOrganization,
    setCurrentPageNumber,
    updateOrganizationCountry,
}) => {
    const translation = useTranslation()

    const { organizationName, address, city, zipCode } = organization

    const [organizationError, setOrganizationError] = useState('')
    const [organizationSuccess, setOrganizationSuccess] = useState(false)
    const [addressError, setAddressError] = useState('')
    const [addressSuccess, setAddressSuccess] = useState(false)
    const [cityError, setCityError] = useState('')
    const [citySuccess, setCitySuccess] = useState(false)
    const [zipCodeError, setZipCodeError] = useState('')
    const [zipCodeSuccess, setZipCodeSuccess] = useState(false)

    const [isFormValidated, setIsFormValidated] = useState(false)

    useEffect(() => {
        if (
            !isValidAddress(address) ||
            !isValidCompanyName(organizationName) ||
            !isValidZipCode(zipCode) ||
            !organization.countryId
        ) {
            return setIsFormValidated(false)
        }
        setIsFormValidated(true)
        // eslint-disable-next-line
    }, [organization])

    const handleCompanyValidation = () => {
        const error = checkCompanyName(organizationName, translation)
        !error && setOrganizationSuccess(true)
        setOrganizationError(error)
    }

    const handleAddressValidation = () => {
        const error = checkAddress(address, translation)
        !error && setAddressSuccess(true)
        setAddressError(error)
    }

    const handleCityValidation = () => {
        const error = checkCity(city, translation)
        !error && setCitySuccess(true)
        setCityError(error)
    }

    const handleZipCodeValidation = () => {
        const error = checkZipCode(zipCode, translation)
        !error && setZipCodeSuccess(true)
        setZipCodeError(error)
    }

    const handleSubmit = (e: any) => {
        e.preventDefault()
        if (isFormValidated) {
            setCurrentPageNumber(2)
        }
    }

    return (
        <Container>
            <HeadingPrimary>
                {translation.reusable.companyInformation}
            </HeadingPrimary>
            <Form onSubmit={handleSubmit}>
                <FormContainer>
                    <FormHalf>
                        <FormField>
                            <PrimaryLabel
                                isError={!!organizationError}
                                isSuccess={organizationSuccess}
                            >
                                {translation.reusable.companyName}
                            </PrimaryLabel>
                            <FormInput40
                                onFocus={() => {
                                    setOrganizationError('')
                                    setOrganizationSuccess(false)
                                }}
                                onBlur={handleCompanyValidation}
                                className={
                                    organizationError
                                        ? 'border-error'
                                        : organizationSuccess
                                        ? 'border-success'
                                        : ''
                                }
                                placeholder={
                                    translation.onboardingFlow.enterCompanyName
                                }
                                type="text"
                                value={organizationName}
                                onChange={(e) =>
                                    updateOrganization(
                                        'organizationName',
                                        e.target.value
                                    )
                                }
                            />
                            {organizationError && (
                                <ErrorMessage>{organizationError}</ErrorMessage>
                            )}
                        </FormField>
                        <FormField>
                            <PrimaryLabel
                                isError={!!addressError}
                                isSuccess={addressSuccess}
                            >
                                {translation.reusable.address}
                            </PrimaryLabel>
                            <FormInput40
                                onFocus={() => {
                                    setAddressError('')
                                    setAddressSuccess(false)
                                }}
                                onBlur={handleAddressValidation}
                                className={
                                    addressError
                                        ? 'border-error'
                                        : addressSuccess
                                        ? 'border-success'
                                        : ''
                                }
                                placeholder={
                                    translation.onboardingFlow.enterAddress
                                }
                                type="text"
                                value={address}
                                onChange={(e) =>
                                    updateOrganization(
                                        'address',
                                        e.target.value
                                    )
                                }
                            />
                            {addressError && (
                                <ErrorMessage>{addressError}</ErrorMessage>
                            )}
                        </FormField>
                    </FormHalf>
                    <FormHalf>
                        <FormField>
                            <PrimaryLabel isError={false} isSuccess={false}>
                                {translation.reusable.country}
                            </PrimaryLabel>
                            <CountryCodeDropdown
                                selectDropdownCB={(country) =>
                                    updateOrganizationCountry(country)
                                }
                                previouslySelectedCountryCode={
                                    organization.countryCode
                                }
                                error={false}
                                success={false}
                                hideCallingCode
                                showCountryName
                            />
                        </FormField>
                        <FormField>
                            <FlexInputContainer>
                                <CityInputContianer>
                                    <PrimaryLabel
                                        isError={!!cityError}
                                        isSuccess={citySuccess}
                                    >
                                        {translation.reusable.city}
                                    </PrimaryLabel>
                                    <FormInput40
                                        onFocus={() => {
                                            setCityError('')
                                            setCitySuccess(false)
                                        }}
                                        onBlur={handleCityValidation}
                                        className={
                                            cityError
                                                ? 'border-error'
                                                : citySuccess
                                                ? 'border-success'
                                                : ''
                                        }
                                        placeholder={
                                            translation.onboardingFlow.enterCity
                                        }
                                        type="text"
                                        value={city}
                                        onChange={(e) =>
                                            updateOrganization(
                                                'city',
                                                e.target.value
                                            )
                                        }
                                    />
                                    {cityError && (
                                        <ErrorMessage>{cityError}</ErrorMessage>
                                    )}
                                </CityInputContianer>
                                <ZipInputContianer>
                                    <PrimaryLabel
                                        isError={!!zipCodeError}
                                        isSuccess={zipCodeSuccess}
                                    >
                                        {translation.reusable.zipCode}
                                    </PrimaryLabel>
                                    <FormInput40
                                        onFocus={() => {
                                            setZipCodeError('')
                                            setZipCodeSuccess(false)
                                        }}
                                        onBlur={handleZipCodeValidation}
                                        className={
                                            zipCodeError
                                                ? 'border-error'
                                                : zipCodeSuccess
                                                ? 'border-success'
                                                : ''
                                        }
                                        placeholder={
                                            translation.onboardingFlow
                                                .enterZipCode
                                        }
                                        type="text"
                                        value={zipCode}
                                        onChange={(e) =>
                                            updateOrganization(
                                                'zipCode',
                                                e.target.value
                                            )
                                        }
                                    />
                                    {zipCodeError && (
                                        <ErrorMessage>
                                            {zipCodeError}
                                        </ErrorMessage>
                                    )}
                                </ZipInputContianer>
                            </FlexInputContainer>
                        </FormField>
                    </FormHalf>
                </FormContainer>
                <NextButtonContainer>
                    <NextButton isActive={isFormValidated} />
                </NextButtonContainer>
            </Form>
        </Container>
    )
}

export default CompanyFormFrame
